'use client';

import { usePopupScrollRef } from '@/components/cms/Popup/PopupContentContext';
import { useEntryEffect } from '@/hooks/useEntryEffect';
import clsx from 'clsx';
import type { ReactNode } from 'react';
import styles from './EntryEffectWrapper.module.scss';

interface EntryEffectWrapperProps {
  enabled: boolean;
  variant?: 'standard' | 'fade' | 'trigger';
  /** Scroll container ref — scopes the entry trigger to a popup/modal instead of the page viewport. */
  root?: React.RefObject<HTMLElement | null>;
  /** Extra class name merged onto the wrapper div (useful for layout sizing). */
  className?: string;
  children: ReactNode;
}

export const EntryEffectWrapper = ({
  enabled,
  variant = 'standard',
  root,
  className,
  children,
}: EntryEffectWrapperProps) => {
  const popupScrollRef = usePopupScrollRef();
  const effectiveRoot = root ?? popupScrollRef ?? undefined;
  const { ref, isVisible } = useEntryEffect({ enabled, root: effectiveRoot });

  if (!enabled) {
    return <>{children}</>;
  }

  return (
    <div
      ref={ref}
      className={clsx(
        className,
        variant !== 'trigger' && styles[variant],
        variant !== 'trigger' && isVisible && styles.visible
      )}
      data-entry-effect={variant === 'trigger' ? '' : undefined}
      data-entry-visible={isVisible || undefined}
    >
      {children}
    </div>
  );
};
